import React, { useCallback, useMemo, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import amplitudeLog from '@client/amplitude';
import {
  AMP_PROJECT_VIEW_HEADER_COST_VISIBILITY,
  AMP_PROJECT_VIEW_HEADER_DISPLAY_CURRENCY,
} from '@shared/constants';
import { DotSeparator } from '@ui/DotSeparator';
import { Icon } from '@ui/Icon';
import { Menu } from '@ui/menus/Menu';
import { BaseMenu } from '@ui/menus/Menu/BaseMenu';
import { SelectTrigger } from '@ui/SelectTrigger';
import {
  DISPLAY_CURRENCY_OPTIONS,
  SelectableDisplayCurrency,
  setCostVisible,
  setDisplayCurrency,
  useDisplayCurrency,
} from '@features/currency/selected-display-currency.hook';
import { NO_CURRENCY } from '@features/currency/display-currency.model';
import {
  RESULT_DISPLAY_UNITS,
  ResultDisplayUnit,
  setResultDisplayUnit,
  useResultDisplayUnit,
} from '@features/results/emission-units.store';
import type { CurrencyBaseUnit } from '@shared/models/units/currency-units.model';

const selectableCurrencySet: ReadonlySet<string> = new Set(
  DISPLAY_CURRENCY_OPTIONS,
);

const UNIT_LABELS: Record<ResultDisplayUnit, string> = {
  total: 'Absolute values',
  GFA: 'Per m² GFA',
};

/** Hidden until DEV-3410 — app-wide absolute vs per m² GFA display. */
const SHOW_DISPLAY_UNIT_MENU = false;

type ResultCardSuffixProps = {
  costEnabled: boolean;
  currencyKey: CurrencyBaseUnit | 'NONE';
  displayUnit: ResultDisplayUnit;
};

const ResultCardSuffix = React.memo<ResultCardSuffixProps>(
  function ResultCardSuffix({ costEnabled, currencyKey, displayUnit }) {
    const { classes } = useStyles();

    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          color: 'rgba(0,0,0,0.6)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            color: 'rgba(0,0,0,0.6)',
            position: 'relative',
            top: -1,
          }}
        >
          <Icon category="costEmission" icon="kgCO2e" size={18} />
        </Box>
        <Typography
          component="span"
          sx={{
            fontSize: 11,
            fontWeight: 400,
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          kgCO
          <Box component="sub" className={classes.sub}>
            2
          </Box>
          e
        </Typography>
        {costEnabled && <DotSeparator />}
        {costEnabled && (
          <Box sx={{ position: 'relative', top: -1 }}>
            <Icon category="currency" icon={currencyKey} size={18} />
          </Box>
        )}
        {costEnabled && displayUnit === 'GFA' && <DotSeparator />}
        {displayUnit === 'GFA' && (
          <Typography className={classes.suffixUnit}>per m² (GFA)</Typography>
        )}
      </Box>
    );
  },
);

/**
 * Result display selector. A single {@link SelectTrigger} opens a combined
 * menu for picking a cost currency and selecting the result display unit
 * (total vs per m² GFA).
 */
export const ResultCard: React.FC<{
  placement?: 'top' | 'bottom';
  nested?: boolean;
}> = ({ placement = 'bottom', nested = false }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const displayCurrency = useDisplayCurrency();
  const selectedDisplayUnit = useResultDisplayUnit();
  // Proposal bars are currently always calculated per GFA. Until the app-wide
  // display-unit control is enabled, keep this selector aligned with those values.
  const displayUnit: ResultDisplayUnit = SHOW_DISPLAY_UNIT_MENU
    ? selectedDisplayUnit
    : 'GFA';

  const currencyKey: CurrencyBaseUnit | 'NONE' = displayCurrency.isEnabled
    ? displayCurrency.current
    : NO_CURRENCY;

  const handleCurrencyChange = useCallback((value: string) => {
    if (!selectableCurrencySet.has(value)) {
      setCostVisible(false);
      amplitudeLog(AMP_PROJECT_VIEW_HEADER_COST_VISIBILITY, { visible: false });
    } else {
      const currency = value as SelectableDisplayCurrency;
      setDisplayCurrency(currency);
      amplitudeLog(AMP_PROJECT_VIEW_HEADER_DISPLAY_CURRENCY, { currency });
    }
    setAnchorEl(null);
  }, []);

  const handleUnitChange = useCallback((unit: ResultDisplayUnit) => {
    setResultDisplayUnit(unit);
    setAnchorEl(null);
  }, []);

  const handleOpen = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => setAnchorEl(e.currentTarget),
    [],
  );
  const handleClose = useCallback(() => setAnchorEl(null), []);

  const costCurrency = displayCurrency.isEnabled
    ? displayCurrency.current
    : null;

  // The generic-carbon convention is not named here: the trigger already
  // carries its icon, and the scope overlay spells it out.
  const tooltipTitle = useMemo(
    () => (
      <>
        Carbon
        {costCurrency !== null && <> &amp; expenses</>}
        {displayUnit === 'GFA'
          ? ' per gross floor area'
          : ' as absolute values'}
      </>
    ),
    [costCurrency, displayUnit],
  );

  const suffix = useMemo(
    () => (
      <ResultCardSuffix
        costEnabled={displayCurrency.isEnabled}
        currencyKey={currencyKey}
        displayUnit={displayUnit}
      />
    ),
    [displayCurrency.isEnabled, currencyKey, displayUnit],
  );

  return (
    <>
      <Tooltip
        title={tooltipTitle}
        placement={placement}
        slotProps={{ tooltip: { style: { textAlign: 'center' } } }}
      >
        <span>
          <SelectTrigger
            suffix={suffix}
            open={Boolean(anchorEl)}
            onClick={handleOpen}
          />
        </span>
      </Tooltip>
      <BaseMenu
        type={nested ? 'submenu' : 'root'}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={
          placement === 'top'
            ? { vertical: 'top', horizontal: 'right' }
            : undefined
        }
        transformOrigin={
          placement === 'top'
            ? { vertical: 'bottom', horizontal: 'right' }
            : undefined
        }
      >
        <Menu.Group label="Expenses">
          {DISPLAY_CURRENCY_OPTIONS.map((currency) => (
            <Menu.Item
              key={currency}
              onClick={() => handleCurrencyChange(currency)}
              selected={
                displayCurrency.isEnabled &&
                displayCurrency.current === currency
              }
              icon={
                displayCurrency.isEnabled &&
                displayCurrency.current === currency ? (
                  <Icon category="mui" icon="Check" />
                ) : undefined
              }
            >
              {currency}
            </Menu.Item>
          ))}
          <Menu.Item
            onClick={() => handleCurrencyChange(NO_CURRENCY)}
            selected={!displayCurrency.isEnabled}
            icon={
              !displayCurrency.isEnabled ? (
                <Icon category="mui" icon="Check" />
              ) : undefined
            }
          >
            Off
          </Menu.Item>
        </Menu.Group>
        {SHOW_DISPLAY_UNIT_MENU && (
          <>
            <Menu.Divider />
            <Menu.Group label="Display as">
              {[...RESULT_DISPLAY_UNITS].reverse().map((unit) => (
                <Menu.Item
                  key={unit}
                  onClick={() => handleUnitChange(unit)}
                  selected={unit === displayUnit}
                  icon={
                    unit === displayUnit ? (
                      <Icon category="mui" icon="Check" />
                    ) : undefined
                  }
                >
                  {UNIT_LABELS[unit]}
                </Menu.Item>
              ))}
            </Menu.Group>
          </>
        )}
      </BaseMenu>
    </>
  );
};

const useStyles = makeStyles()(() => ({
  suffixUnit: {
    fontSize: 11,
    fontWeight: 400,
    letterSpacing: '0.3px',
    lineHeight: 1,
    color: 'rgba(0,0,0,0.6)',
    whiteSpace: 'nowrap',
  },
  sub: {
    fontSize: '0.7em',
    verticalAlign: 'sub',
    lineHeight: 0,
  },
}));
